/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { produce } from 'immer';
import {
  ActorRefFrom,
  SnapshotFrom,
  assertEvent,
  assign,
  emit,
  enqueueActions,
  fromPromise,
  sendTo,
  setup,
} from 'xstate';

import { cacheUtils } from '@eluve/apollo-client';
import { scheduleBackgroundTaskAction } from '@eluve/appointment-tasks';
import { setupEluveMediaStreams } from '@eluve/components';
import { formatErrorForLogging } from '@eluve/eluve-errors';
import {
  appointmentSegmentsFragment,
  segmentTranscriptFragment,
} from '@eluve/frontend-appointment-hooks';
import {
  preventBrowserClose,
  toastAction,
} from '@eluve/frontend-machine-utils';
import { keepScreenAwake } from '@eluve/frontend-machine-utils';
import { AppointmentFileTypesLookup } from '@eluve/graphql-types';
import { createCorrelatedLogger } from '@eluve/logger';
import { getSupportedAudioFormat } from '@eluve/user-local-files';
import {
  createStableUuid,
  getChunkNumberFromFileName,
  isAudioFileChunkFile,
  makeStandardAudioFileName,
} from '@eluve/utils';

import { MuteDetector } from '../../MuteDetector';
import { scheduleFileUploadAction } from '../../actions';
import {
  MediaRecorderEvents,
  MicrophoneEvents,
  PrepareAppointmentAudioRecordsInput,
  PrepareAppointmentAudioRecordsResponse,
  WebSocketEvents,
  audioFileSystem,
  getAuthToken,
  mediaRecorder,
  validateAudioCapture,
  websocket,
} from '../../actors';
import { lockManagerActor } from '../../actors/lockManagerActor';
import { createNewAudioPacketSubject } from '../../utils';

import { getCurrentTranscript } from './getCurrentTranscript';
import {
  AudioHealthEvents,
  Context,
  Input,
  TranscriptionMachineEvents,
} from './types';

export const CONTINUED_USER_PRESENCE_RESPONSE_WINDOW_MS = 1000 * 60; // 1 minute
export const CONTINUED_USER_PRESENCE_TIMEOUT_MS =
  1000 * 60 * 30 - CONTINUED_USER_PRESENCE_RESPONSE_WINDOW_MS; // 30 minutes minus response window
export const INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS = 1000 * 60 * 3; // 3 minutes
export const INITIAL_USER_PRESENCE_TIMEOUT_MS =
  1000 * 60 * 60 * 2 - INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS; // 2 hours minus response window

export const FINALIZING_RECORDING_TIMEOUT_MS = 10;
const FINALIZING_RECORDING_TIMEOUT_FOR_WEBWORKER_MS = 1500;

const WEBSOCKET_ACTOR = 'websocket';
const MEDIA_RECORDER_ACTOR = 'mediaRecorder' as const;

const LOCK_MANAGER_ACTOR = 'lockManagerActor';

const LOW_CONFIDENCE_DIALOGUE_THRESHOLD = 0.8;
const LOW_CONFIDENCE_DIALOGUE_WINDOW_SIZE = 7;

type EmittedEvents =
  | { type: 'recording.started' }
  | { type: 'recording.failed' }
  | { type: 'paused' }
  | { type: 'stopped' }
  | { type: 'idle' }
  | { type: 'checkingUserPresence' }
  | { type: 'disclaimer' }
  | { type: 'transcription.degraded'; isDegraded: boolean }
  | { type: 'mediaStream.set' }
  | WebSocketEvents
  | MicrophoneEvents
  | AudioHealthEvents;

export const transcriptionMachine = setup({
  types: {
    context: {} as Context,
    events: {} as TranscriptionMachineEvents,
    input: {} as Input,
    emitted: {} as EmittedEvents,
  },
  guards: {
    areStreamsSetup: () => Boolean(window?.eluveMediaStreams),
    hasNonSupportedBrowser: ({ context }) =>
      context.supportedAudioFormat === undefined,
    isFirstRecordingFailure: ({ context }) => context.recordingRetryCount <= 1,
    needsToPlayDisclaimer: ({ context }) => Boolean(context.playDisclaimer),
    isInChunkingMode: ({ context }) =>
      Boolean(context.chunkedBatchTranscriptionEnabled),
    isPausing: ({ context }) => context.isPausing,
    isStopping: ({ context }) => context.isStopping,
    pauseFinalized: ({ context }) => {
      const arePendingFilesFinalized =
        context.filesPendingCompletion.length === 0;

      return (
        context.isPausing &&
        context.isRecordingCleanupFinalized &&
        arePendingFilesFinalized
      );
    },
    stopFinalized: ({ context }) => {
      const arePendingFilesFinalized =
        context.filesPendingCompletion.length === 0;

      return (
        context.isStopping &&
        context.isRecordingCleanupFinalized &&
        arePendingFilesFinalized
      );
    },
    isLiveTranscriptionEnabled: ({ context }) =>
      context.isUsingLiveTranscription === true,
  },
  actors: {
    websocket,
    mediaRecorder,
    audioFileSystem,
    preventBrowserClose,
    keepScreenAwake,
    getAuthToken,
    validateAudioCapture,
    prepareAppointmentAudioRecords: fromPromise<
      PrepareAppointmentAudioRecordsResponse,
      PrepareAppointmentAudioRecordsInput
    >(async () => {
      return {
        resumableUploadSessionUri: undefined,
        nextResumableUploadSessionUri: undefined,
      };
    }),
    lockManagerActor,
  },
  actions: {
    setupMediaStreams: () => {
      if (!window?.eluveMediaStreams) {
        setupEluveMediaStreams();
      }
    },
    incrementRecordingRetryCount: assign({
      recordingRetryCount: ({ context }) => context.recordingRetryCount + 1,
    }),
    toast: toastAction,
    logError: (_machine: unknown, params: { message: string }) => {
      const {
        context: { logger },
      } = _machine as { context: Context };
      logger.error(params.message);
    },

    captureException: ({ event, context }) => {
      if ('error' in event && event.error) {
        const errorMessage = event.error?.message ?? event.error;

        context.logger.error('Recording Exception', {
          error: errorMessage,
          event,
          appointmentId: context.sessionAppointmentId,
          segmentId: context.segmentId,
        });
      }
    },

    captureWarning: ({ event, context }) => {
      const message = (() => {
        if ('message' in event) {
          return event.message;
        }
        if ('error' in event && event.error && event.error instanceof Error) {
          return formatErrorForLogging(event.error);
        }

        return '';
      })();

      context.logger.warn(`Recording Warning: ${message}`, {
        event,
        appointmentId: context.sessionAppointmentId,
        segmentId: context.segmentId,
      });
    },

    onAudioSilent: enqueueActions(({ enqueue, event }) => {
      assertEvent(event, ['audio.silent']);
      enqueue.assign({ hasValidAudioSource: false });
      enqueue.emit(event);
    }),
    onAudioSoundDetected: enqueueActions(({ enqueue, event }) => {
      assertEvent(event, ['audio.soundDetected']);
      enqueue.assign({ hasValidAudioSource: true });
      enqueue.emit(event);
    }),

    setSession: assign(({ event }) => {
      assertEvent(event, ['START']);
      return {
        sessionAppointmentId: event.appointmentId,
        microphoneName: event.microphoneName,
        deviceId: event.deviceId,
        playDisclaimer: event.playDisclaimer,
        recordingFailed: false,
        recordingRetryCount: 0,
        appointmentId: event.appointmentId,
        confidenceScoreWindow: [],
        isLowConfidenceTranscription: false,
        totalRecordingDurationMs: event.initialTotalRecordingDurationMs ?? 0,
      };
    }),

    clearSession: assign(() => {
      return {
        sessionAppointmentId: undefined,
        transcript: undefined,
        playDisclaimer: undefined,
        recordingRetryCount: 0,
        recordingFailed: false,
        appointmentId: undefined,
        confidenceScoreWindow: [],
        isLowConfidenceTranscription: false,
        totalRecordingDurationMs: 0,
      };
    }),
    prepareRecording: enqueueActions(({ enqueue, context }) => {
      const recordingStartedAt = new Date().toISOString();

      // We only want to treat the initial file as chunked if both chunking is enabled AND
      // we're using web worker audio. Legacy chunked recording still expects to create a full batch file here
      const isInitialFileChunked =
        context.chunkedBatchTranscriptionEnabled &&
        context.isUsingWebWorkerAudioRecording;

      const initialFileName = makeStandardAudioFileName({
        recordingStartedAt,
        isChunkingEnabled: isInitialFileChunked,
        chunkNum: 0,
      });

      enqueue.assign({
        recordingFileName: initialFileName,
        recordingStartedAt,
        segmentId: createStableUuid(recordingStartedAt),
        transcript: ({ context }) =>
          getCurrentTranscript(context.sessionAppointmentId!).transcript,
        interimTranscript: '',
        audioPacketSubject: createNewAudioPacketSubject,
        microphoneStatus: undefined,
      });

      enqueue('updateSegmentCache');
    }),
    updateSegmentCache: ({ context }) => {
      const { appointmentId, segmentId, recordingStartedAt } = context;
      if (appointmentId && segmentId) {
        cacheUtils.updateFragment(
          {
            fragment: appointmentSegmentsFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }
            return produce(existing, (draft) => {
              draft.segments.push({
                __typename: 'AppointmentSegments' as const,
                id: segmentId,
                transcript: '',
                recordingStartedAt,
                recordingDuration: null,
                updatedAt: new Date().toISOString(),
              });
            });
          },
        );
      }
    },

    setDeviceId: assign(({ context, event }) => {
      assertEvent(event, ['RESUME']);
      return { deviceId: event.deviceId ?? context.deviceId };
    }),

    setMediaStream: enqueueActions(({ event, enqueue }) => {
      assertEvent(event, ['recorder.started']);
      enqueue.assign({ mediaStream: event.mediaStream });
      enqueue.emit({ type: 'mediaStream.set' });
    }),

    startWebSocket: sendTo(WEBSOCKET_ACTOR, ({ context }) => {
      return { type: 'connection.connect', authToken: context.authToken };
    }),

    onMicrophoneDisconnected: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        microphoneStatus: 'disconnected',
      });
      enqueue.emit({ type: 'microphone.disconnected' });
    }),

    onMicrophoneMuted: enqueueActions(({ enqueue }) => {
      enqueue({
        type: 'toast',
        params: { type: 'warning', message: 'Your microphone is muted.' },
      });
      enqueue.emit({ type: 'microphone.muted' });
      enqueue.assign({ microphoneStatus: 'muted' });
    }),

    onMicrophoneUnmuted: enqueueActions(({ enqueue }) => {
      enqueue.emit({ type: 'microphone.unmuted' });
      enqueue.assign({ microphoneStatus: 'connected' });
    }),

    playDisclaimerSound: enqueueActions(({ enqueue }) => {
      enqueue.emit({ type: 'disclaimer' });
    }),

    fileCreated: enqueueActions(({ event, context, enqueue }) => {
      assertEvent(event, ['fileSystem.fileCreated']);
      enqueue.assign({
        lastObservedChunkFileName: event.fileName,
      });

      enqueue.sendTo(LOCK_MANAGER_ACTOR, event);

      const isChunkedFile = isAudioFileChunkFile(event.fileName);

      if (!context.filesPendingCompletion.includes(event.fileName)) {
        enqueue.assign({
          filesPendingCompletion: [
            ...context.filesPendingCompletion,
            event.fileName,
          ],
        });
      }

      if (context.chunkedBatchTranscriptionEnabled && isChunkedFile) {
        enqueue.assign({
          audioChunkFiles: [
            ...context.audioChunkFiles,
            { fileName: event.fileName, chunkNum: event.chunkNum },
          ],
        });
      }
    }),

    processCompletedChunk: enqueueActions(({ context, event, enqueue }) => {
      assertEvent(event, ['fileSystem.fileReady']);

      enqueue.sendTo(LOCK_MANAGER_ACTOR, event);

      enqueue({
        type: 'scheduleFileUpload',
        params: {
          type: 'TASK.UPLOAD_FILE',
          uploadType: isAudioFileChunkFile(event.fileName)
            ? AppointmentFileTypesLookup.CHUNKED_AUDIO
            : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
          chunkDurationMinutes: context.chunkDurationMinutes,
          appointmentId: context.sessionAppointmentId!,
          fileName: event.fileName,
          chunkNum: event.chunkNum,
          format: context.supportedAudioFormat!,
          wasDegraded: context.isTranscriptionDegraded ?? false,
          shouldBeTranscribed: true,
          recordingStartedAt: context.recordingStartedAt,
          attemptNumber: 1,
        },
      });

      enqueue.assign({
        audioChunkFiles: context.audioChunkFiles.filter(
          (chunkFile) => chunkFile.fileName !== event.fileName,
        ),
        filesPendingCompletion: context.filesPendingCompletion.filter(
          (fileName) => fileName !== event.fileName,
        ),
      });
    }),

    scheduleFileUpload: scheduleFileUploadAction,
    scheduleBackgroundTask: scheduleBackgroundTaskAction,

    stopRecording: enqueueActions(({ enqueue, context }) => {
      context.mediaStream?.getTracks().forEach((track) => track.stop());
      context.audioPacketSubject?.complete();

      if (context.lastObservedChunkFileName) {
        enqueue({
          type: 'scheduleBackgroundTask',
          params: {
            type: 'TASK.MARK_CHUNK_FINAL',
            appointmentId: context.sessionAppointmentId!,
            fileName: context.lastObservedChunkFileName!,
            userId: context.userId,
          },
        });
      }

      if (context.recordingFileName && context.sessionAppointmentId) {
        enqueue({
          type: 'scheduleFileUpload',
          params: {
            type: 'TASK.UPLOAD_FILE',
            uploadType: isAudioFileChunkFile(context.recordingFileName)
              ? AppointmentFileTypesLookup.CHUNKED_AUDIO
              : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
            appointmentId: context.sessionAppointmentId,
            fileName: context.recordingFileName,
            format: context.supportedAudioFormat!,
            wasDegraded: context.isTranscriptionDegraded ?? false,
            shouldBeTranscribed:
              // If we're recording with web worker audio we always want to transcribe here
              Boolean(
                context.isUsingWebWorkerAudioRecording ||
                  !context.chunkedBatchTranscriptionEnabled,
              ),
            recordingStartedAt: context.recordingStartedAt,
            attemptNumber: 1,
          },
        });
      } else {
        enqueue({
          type: 'logError',
          params: {
            message:
              'No recording file name or appointment ID available when stopping recording',
          },
        });
      }

      enqueue({
        type: 'scheduleBackgroundTask',
        params: {
          type: 'TASK.UPDATE_SEGMENT_RECORDING_ENDED_AT',
          appointmentId: context.sessionAppointmentId!,
          segmentId: context.segmentId!,
          recordingEndedAt: new Date().toISOString(),
        },
      });

      for (const chunkFile of context.audioChunkFiles) {
        enqueue({
          type: 'scheduleFileUpload',
          params: {
            uploadType: AppointmentFileTypesLookup.CHUNKED_AUDIO,
            chunkDurationMinutes: context.chunkDurationMinutes,
            type: 'TASK.UPLOAD_FILE',
            appointmentId: context.sessionAppointmentId!,
            fileName: chunkFile.fileName,
            chunkNum: chunkFile.chunkNum,
            format: context.supportedAudioFormat!,
            wasDegraded: context.isTranscriptionDegraded ?? false,
            shouldBeTranscribed: true,
            recordingStartedAt: context.recordingStartedAt,
            attemptNumber: 1,
          },
        });
      }

      enqueue.emit({
        type: 'transcription.degraded',
        isDegraded: false,
      });

      enqueue.assign({
        segmentId: undefined,
        mediaStream: undefined,
        interimTranscript: undefined,
        authToken: undefined,
        recordingFileName: undefined,
        isTranscriptionDegraded: undefined,
        audioPacketSubject: undefined,
        microphoneStatus: undefined,
        isPausing: false,
        isStopping: false,
        filesPendingCompletion: [],
        audioChunkFiles: [],
        isRecordingCleanupFinalized: false,
      });
    }),

    updateTranscription: assign(({ context, event }) => {
      assertEvent(event, ['connection.transcript.received']);
      const { segmentId } = context;

      const {
        transcript: { isFinal, transcript },
      } = event;

      if (isFinal) {
        if (segmentId) {
          // Push the new transcript into Apollo cache so it can be consumed by other
          // components
          cacheUtils.updateFragment(
            {
              fragment: segmentTranscriptFragment,
              key: {
                id: segmentId,
              },
            },
            (existing) => {
              return {
                __typename: 'AppointmentSegments' as const,
                id: segmentId,
                transcript: [existing?.transcript, transcript]
                  .filter(Boolean)
                  .join(' ')
                  .trim(),
                updatedAt: new Date().toISOString(),
              };
            },
          );
        }

        return {
          transcript: [context.transcript, transcript]
            .filter(Boolean)
            .join(' '),
          interimTranscript: '',
        };
      }

      return {
        interimTranscript: transcript,
      };
    }),

    updateLowConfidenceState: assign(({ context, event }) => {
      assertEvent(event, ['connection.transcript.received']);

      const confidence = event.transcript?.confidence;
      const prevWindow = context.confidenceScoreWindow ?? [];

      const newWindow =
        typeof confidence === 'number'
          ? [...prevWindow, confidence].slice(
              -LOW_CONFIDENCE_DIALOGUE_WINDOW_SIZE,
            )
          : prevWindow;

      const isLowConfidenceTranscription =
        newWindow.length === LOW_CONFIDENCE_DIALOGUE_WINDOW_SIZE &&
        newWindow.every((score) => score < LOW_CONFIDENCE_DIALOGUE_THRESHOLD);

      return {
        confidenceScoreWindow: newWindow,
        isLowConfidenceTranscription,
      };
    }),

    setTranscriptionDegraded: enqueueActions(({ enqueue, context, event }) => {
      context.logger.warn('Live Transcription degraded', {
        appointmentId: context.sessionAppointmentId,
        segmentId: context.segmentId,
        event,
      });

      enqueue.emit({
        type: 'transcription.degraded',
        isDegraded: true,
      });

      enqueue.assign({
        isTranscriptionDegraded: true,
      });
    }),

    onRecordingFailed: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        recordingFailed: true,
      });
      enqueue.emit({ type: 'recording.failed' });
    }),

    shortenUserPresenceDelays: assign({
      userPresenceResponseWindowMs: CONTINUED_USER_PRESENCE_RESPONSE_WINDOW_MS,
      userPresenceTimeoutMs: CONTINUED_USER_PRESENCE_TIMEOUT_MS,
    }),

    resetUserPresenceDelays: assign({
      userPresenceResponseWindowMs: INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS,
      userPresenceTimeoutMs: INITIAL_USER_PRESENCE_TIMEOUT_MS,
    }),

    logStartStopEvents: ({ context }) => {
      const now = Date.now();
      if (context.recordingStartedAt) {
        const elapsed = now - new Date(context.recordingStartedAt).getTime();
        if (elapsed < 1000) {
          context.logger.warn('Transcription stopped in less than 1 second', {
            elapsed,
            appointmentId: context.sessionAppointmentId,
          });
        } else if (elapsed < 3000) {
          context.logger.info('Transcription stopped in less than 3 seconds', {
            elapsed,
            appointmentId: context.sessionAppointmentId,
          });
        }
      }
    },
    onPaused: enqueueActions(({ enqueue, context }) => {
      const totalRecordingDurationMs =
        context.totalRecordingDurationMs +
        (new Date().getTime() - new Date(context.recordingStartedAt).getTime());

      enqueue.assign({
        totalRecordingDurationMs,
      });

      enqueue.emit({ type: 'paused' });
    }),

    sendStopMessageToMediaRecorder: sendTo(
      MEDIA_RECORDER_ACTOR,
      () =>
        ({
          type: 'recorder.stop',
        }) satisfies MediaRecorderEvents,
    ),

    onClientAudioCompletedAt: enqueueActions(({ enqueue, context }) => {
      const clientCompletedAt = new Date().toISOString();
      if (context.recordingFileName && context.sessionAppointmentId) {
        enqueue({
          type: 'scheduleBackgroundTask',
          params: {
            type: 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT',
            appointmentId: context.sessionAppointmentId,
            clientCompletedAt,
            fileId: context.recordingFileName,
          },
        });
      }

      if (context.lastObservedChunkFileName && context.sessionAppointmentId) {
        enqueue({
          type: 'scheduleBackgroundTask',
          params: {
            type: 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT',
            appointmentId: context.sessionAppointmentId,
            clientCompletedAt,
            fileId: context.lastObservedChunkFileName,
          },
        });
      }
    }),
    onRollingToNewChunk: enqueueActions(({ enqueue, event, context }) => {
      assertEvent(event, ['fileSystem.rollingToNewChunk']);
      enqueue({
        type: 'scheduleBackgroundTask',
        params: {
          type: 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT',
          appointmentId: context.sessionAppointmentId!,
          clientCompletedAt: new Date().toISOString(),
          fileId: event.completedChunkName,
        },
      });
    }),

    onPausing: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        isPausing: true,
      });

      enqueue('sendStopMessageToMediaRecorder');
      enqueue.raise({ type: 'CLIENT_AUDIO_COMPLETED_AT' });
    }),

    onStopping: enqueueActions(({ enqueue, event }) => {
      assertEvent(event, ['STOP']);
      enqueue.assign({
        isStopping: true,
      });

      enqueue('sendStopMessageToMediaRecorder');
      enqueue.emit({ type: 'stopped' });
      enqueue('logStartStopEvents');
      enqueue.raise({ type: 'CLIENT_AUDIO_COMPLETED_AT' });
    }),
  },
  delays: {
    userPresenceResponseWindow: ({ context }) => {
      return context.userPresenceResponseWindowMs;
    },
    userPresenceTimeout: ({ context }) => {
      return context.userPresenceTimeoutMs;
    },
    finalizingAudioTimeout: ({ context }) => {
      return context.finalizingAudioTimeoutInMs;
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QBUBOBDAdrAxqglgA4Au+A9pgHQCSEANmAMQDKyAggErIDaADALqJQhMrHykKQkAA9EAWgCcvAKyUAjADYA7AA4tAFn1aDO5QBoQAT3n6llHQoUBmLQs0AmHRodaAvr4s0LFwCEnIqWgYWdi5uNUEkEBExCUwpWQRFXl5KfWUNZX1eW3citQULa0zlLTV1JxV3LSc1TQ0nBX9AjGw8IlSaeiYAYQB5ADkAMWoOAFkAfXHR5ABReYBxFfGVjjZkagm+BOFRcXD0+TV3XidKZTV83k0dNR0mp0r5a9vlBXunJw1NRadqFLogIK9UIDSIjCbTOaLZZrTbbXb7Q7xKTJM6SRIZOStByUJ7PXilQGeDSfTJ6SiuPIM9xuDRXdzgyEhfrhQZRNgAVQAIgd5gA1NgAGWogr2B3G8w4KwAivyVqwVoKjtjTqkLpk1NkNJRPACtO5SqYnE0acpzfYHO4De5bU0DByelywhQaJhmHAxN6OGAcGRUBB8JgoIxFWNxtthjwBNqUud8Yh9O5bo6FKVyWonBoM+YrIhWtl6S9TMVAQC-u7gn0vRFff6eUGQ2GI1GAGb4BjMSywYhgAC2lF7DCD6Agli1iRxurT+sB9JU9wNHQUBhpakMRt4On0Xn0r3NBXZAQhHsbMJbsADVHbofDkcYE7AA6Ho8oqDIdDoXbIGQ4xgAA7sMAAWACumAANZzicKZ4qAGQFrcOgFu05oPK8xQ7t4OiUAojKFIWBTkjo9ZQty3rUHeD6UE+navu+n7DmO77DKgYDoMOEAIUkOqpihiDmvo6gKBoJq2q4+RODoO76AUlBSe4Um8IW9yOFRnq3n695tsGz5dowwxSlsyDzAKwqjPMYyzAAChKKyrIKVmJscglIWkiRVHIyg1PS2gvNcIJaOS+gWBkTTKLc3gab8GjkrwCgYTpN48nR+kMUxL5QJQsyQPg6C5WAqCMNxHYQGVlBlb+qACQuwkyIghYqdkehvBohYgi4NJyM09glIyNyxfkF7dA20KZfRhlVV2BVFSVRlhmVjAjvgeBkIQEEUGAlDhrgFCYMGfGNUJyEtQgmYKERnjOBhDxGA4-X6Lcu7EQeDwtDoKjpdNtGzYGK15Yt4bLVVa0bVtO17ZQI5QWdSbzhdPkiQgu5vHc8mGMRkkaFuxZ+ThJIni0ZqtE9B7-TRzbZXNxmRmDxWleV0O-rDJ2UDBCNI55TWXRku6DUp5JaMoKiOAa1Ilpkng5ElVxXLUDS-E4+g002Pr08D81M4V4Os5QEpkNOJmVc+NVDugqB88muJo1dfxaHczIGrob1uCl-WvEaBqtFaBNvborya3pra64z+VsFB4ZkJMfYfoO7FvonbHfnVobnd5epGDkzTEQTqWAuuROIL9uTeLofyF2TYczTrj4gwtMdxwn-bJ9+Jtmyxaed2O3HTrOyOIQ7ep-Kotq2ieBhbklkWy7uRoE7FAKxbUxgFvXgON4xzdMwA6mAABGsBkDgsFgMQlCQcGsFdtQ3YrJg6DHwwECMNnY9Lhu4nr0W6sWReBpG8QizhWTAkgR0Vk286YRybnrfKR9T7n0vtfW+F8H5Pxfm-SAn8sQoxzj-QEdQjzdXVqFAmClZagKIgWVoG98ySQmleKatNtbwL3ogygyCz4XyvpQZ+r936MBDJgE6OABjEGvNCH8wYwD4AAG6QC-oudGRJ87tFSh7IBMsqi7nyOoQohdagYU6JeTkGUd6cNygtXhqCBFCNwR-MREiBiHVcadSA3MTrSEIF4+gw9+aoz1A8Jo9JPAvDXgCN4ikCYqRaBLNe+RbCwI4QZSOzEkEnz4WgwROCRGeMkTyWAYBMAQFbuQBy6B+HX27OgRO-ER5eW-uo4iLsnhuAwt1VkPg4m3XaJoQohhQG-DSVlGx+9skoNqfk4ReCikDBgugRRDS6DzNUc1DIeh3C5C8IWZ28k2SKQwuoA8ktHDMgPH4CxMj2ETIyQgqOPCckOOvk49+lAY7EAgmU0gOBeI8m7nlRgEA4YRkUWQS+lBLEAzgY8rhzz7GzI+d475vzMD-MBd6YFXYEAQvPtizARxNmC1LIUHIYk-jmg0sCD4i8mhgN4LPAES91bjKBk8rJLyZl5NRRAL5iMMVYoGLi18mdUCUEIOs4g3ZQxjlhfczliLuXIr5QUtFQq-mbSJcbU2eV8WYEhQC1IJLmkC0dkLVo4k8wYVKLFSSOYdz5hdt4dSzoXj5ieBy3etjD6vJRRqgVwxjqnSBfqkyiyeT1MaaSy1okDyESkmvUx5zUrOozOoNSiUdC-TcLFH1kzuFqscUGm+obik4oja+KN3pFkqPNSEpc1wlKUA6G7TQGYNzuB3M6F2GFxZeG0UUSity2FaweTlKZlB+SlNQA5bipTMA4H2qMWC6BLCMGkNbYclB0DdmHKgAAFFBOdC64BlJXcgfAI4wBkERgASkYIqidyq-X5VnWVc9S6V2UDXRuuNoTDwu2MLobNzRbBXBpIYHIGZwpUiri0P6Y7qKvt9dOz987F2Xv2hg++r5Yz7HGKqQDzbyZ3ANLmrwDgvDbllsyI0tQJ4ZhcFcdohaEXvpnWe7Dy7cO-Mwa+ByApmArFI203cJJnBHhcIWPI8kaRSX3FEzpNxvAng41O7hmHv04ZvgJ-DUZt3EF4vtfdh6T08YvXxoMsARDYDAAfCMYLQJPpfeHTjGGrM-v43fLs4mroGPEoCM0AVwpdO6oph4uRSjESrC8ODmmGbcocmUvKIFpDEEgjBWCvImCsX7uOROXEeJ20Ia0q65ozR3BBEoLw8lWQLz8lV40W5bBgYPFo5QSXMmg1S+UrsGWsvQTgpQBdZBFH4AfINsAmXstwVBeCo1UL9ruYbkW55-X0uzeGzlsbv5JvTcjEN+bsFDXGqJWa4JRD0bXCuMaWohoGg3AwmXZcOQ6u2GehTX6LC1vWM89wrbM25sjdy+Nw74QQe7YWxKqVMq5WoAVXctDG2Utpeh6d-bE2ptQ+Ozt0753CWmoEAFoWxhgsGHaEpHM1y3s5HJGeasR4YOOBST1rloME4vwAgALy7O+iqIMao4AYFgKChB5i9h5-gXnDbrsVaFkpQiVJnAsnkkoPRiBAQM6MOLZ66tDA3MmqhjzWnnnc-QHzgX07YSMGE-yUTZPSzlCzFVowfwMxvHpVUBruQDwBXzFXX4agOcqq5xGK3subfcLt6wUYDlncIE3g9lnJotxKC0DSP3I7A8Fj0L8ZDJvdLrcBxbyP1vIxcct5XqMSejD7i3CaE0rGbTXCCm4EwwIQRNDD9Xiv0eq-Tpr4PuvBDR5qKukeF2bPaj3FGpmLPstTEqSq2BzckljesNN6X833KR-86H9wg-JluDuAV5Pgk917AaW0GpB4W4DBveUKYf3HRbQEwLB7Pvw+B+H6gP3jLv-lujumZgemVEetLlHv-pUmQNerevesQG5ijmbslhHkATHuXhgZGEnuFDkDUCXJuPcLYD7q1Myh3m0OaL9MUH9igbvmgQtCfkflgdASZMZqZnuuAcelAbXrAfAXeo+s+nQQDnvugawcwfvn-v5uPi0pfqWClCSIWHoIOr0uFE1ogBvPYGaElFaKlCFBoD-sflIRIWIbXiASZruuZhATwaPnwTegIUgUIeOqgb1owcYQAb-tgVAGfhflsl8NVq9o4Cpq0IUDSJoTssFIeP8NTChiXiITyNUqengoqMwPyLMGJo2jdldHICeB9q0JmKUJmAUF4G9k0IxkpM0KyDcB1OxrEVYvCgxIkaUh-PHonpkYrvIOQlJqaB7BLBmFrhjClLsmpLUM6GyKxmHk0Xgq0XEL4WSnLG4PYEFmpORNkGpDSHFmcr9roECC8Fvv9g0QkegEkR-MKMwLMNQMwMwPMKJlcXKEnoSFjGEvJIeMyMYIeKQQgCeOJKyPruRMYBnoYVHJMGsngg8Q0HUF4C6uUH8O0BpJ8VJPSJoAWJJCoE8DcLuECVkiCY0vgnMfGnLMYEYt7i-jEvkHRvoraGcn8A0MYKXL3nUXCukgxI3M-NVB-A8ZpG2i4G4ICN4JJG9q8W2rFA8MykYPJGaGHmwKBA0qQJGOsGUmVKZuMGQMOCGpgL2EjkSvbhwKMMMCsBqPMAfNQMgAABJIirAbBbA7CyiHDtFyH6gfZJSonMhUYv7UJVCmCqA05uxGC-CZjdaMlKq7zSmyldgKknQYDDgqlqkUCakjjanDBsDjD6kSgWkojWnoj3H2l+EIBoQqR5DZDZA8l-A3A7jkhGgOoHhjHyzFBh4ABCYAiOYAgunJNqpghZL+OEb0b2-k3g9ojQZE6srIBhQZqOCKjZzZrZMhFqeocgakdwNwhgFoHUjoNQr0C5BMUkosoCzgzokx6ylgXYgoU2ouDSt65UTkbAAAmpQGcWZGwNQOkRwIIuMIKBqEnjdHdK0HFsCIeN7huWAkpIUdWY6BTkCeImGq+EngFLslJB2XSbFMyM6i8LkOFL8G8MYJiWOS4VQOeoQDbJgVkotlzAStCgccyQkdxARQQCYXigSiauEFdvbA6dcJPFuTPI6G8HJD7JJO1A4ATOLEMW4JMdRYRXReKqgPVPDrxIjsjs4fQd6PheJR4YgkToxRQMxeVqxRpMaC4C-rjIUA4OoZkD9OoCCOSfkHoLUcXvUeWhqfgEjoNqqWABGUqakDqXqQaW5MaWaemVaWiLaeMA8fkTkCFCeIWK8Gxp8V6jfqYNZaKTyRrDhTyOqfGc5cOG5VGeEKZMmamf5aiDaRiMFTmfMYSLuLssCDUMUI-kbiZc6AMmaPdI6JAoYGkqKFHhAICpGLAcMOgCQFBNxIwNZCKOKFKDKMVQqMqKqOqJqKVQSYSI6EaCQhZTJtJp8SCMFq8M4NkLYMXKOrZUyR1QBF1XKVAL1f1cQINUwMcXHJQGfDBBAIKFfAEg8T4G2hntPnkHsWoIpnaJ0l2YYPkBAu1Z1d1edbHOQH1QNUNbdeQPdYnJiiFeUKoKyFaG9FJOLJ8XtURGxuQilAYPmP4JeJgGQNVPAIkAcSxbmYSC0OJI6EYMBZvLuP1FEfUMUGocyvkETSlbREMNTWVboIRG9CtXrnVf1C2uoK8AFMmq8VuE4BzgLQtc0HFBQjmKRI9L9bLANIFHCbFNoJoPmFvLzYca4ZGErXqNoEaLRt1PjM4FaP1AFLdP7CMU1XoFcFiaDAbCzMLqgBbUuKFquGUM6LaMRC-v1FaArKSEESko0ArSbZRWbflN7RDJbJKmKlAP7ejCnpzVcLBWHR6ZcO0Fmt9YUCHkXJ7QtCnUbO+lnYFpLVcjRhmEePcL2ciSXS-mXSyNpAnZOgwUzLAe3EnF+COHXRkGaOJDbVuZ-g7QyhJOvPdEHL9G1b3W+tOoPX3CPXqj3JndpbmcYLslPXbQCD2ovK2ivACDWBvJUZXQPZDfHJvexDwgQGdWPaWOaLcMlDubuEUDFefTSdEtfcbYdcGWjqDCWsQG-cnsyEsQHr8AFCfQMc0DPgWBpDmEoJLIGSA+OaIXYgGnknhlgvylA6BrA16RPIgyApuVCZvq8B0LfdMrkgIiebAPMhAFAw-rdM6L8dJqYJRlQ96ag+NCxlCQwzykw+8kGiQxXLQ1cIeLohsdFm6s6LYLmjUBpqvehsWvg6Wmw4Kj8tqhpY7LOUuLQrI54JBm6uWRLOoF4GufBb9Oypo2A3g7yro84vo8KjqqKtWrvRPrmdZURAbXI5Y8AovB9PUHud1K6NZWIxA3Mh4+ioY7qjie-BwyifSKmkuQo+E9VlovnroACMyuYtg7heHq4xIwk58uqW4s1CY+optSSJtZ1KEwMQaIUGclaM6KNF9KOaU4pZzhU28lU94jU2GlWjvRwyFE00oTos8G07uHUDoS1cOjEf0-EUneI8M-yvZbU96Kk5AFA0UcFmpNPvI-M86spN2oUBVTmLmnEzo5I3o2M5WlQC83xEc1aCrlaMg+c1YwyoFAOi-lQgYGs9vnEabYM0zDprxiuhwxBsaJpLmvmL9DUEvlUNcFwyjXfsBo4JKc42XtyjC9Zr+v+lUP4-MX+cMUi1Eqi8YNnuUCpMwtalLJuH0+C3ZX3Zs8Sz5vpn5ubXvfMS1YfcMrYDcJntaLLCeN6WpA9C8BAmNGI8DvjqDjlvCwuS4EpMObTgXhHZJq4CYHri0EoAFEqxjiqzDrlrCFM3YD879K4FJKSSZfOcUG2tcIeGuGo2pmawNha1jhDrjhQJjmDkc68DPpLHuA4IaM6mhbFIeEeKKVcC4GI0wapVHCQ86Knq8WYq4MyjSK6URAHnJp2ujVgxy0yVy1C-lKm1xta4KwSQaDcCpLaGSXueFtBgofJP8A0PG4UPHes5C+U0zDW54eIX47IbmalKoFVnForESKfVUBLKoGuPPtSm8JoJMccc0Z87sozVFX0WJAu6JF4G2p0i8YbWEslQO4nVWwc+w-W3OYYBRi-niwFCoKMmEbshicyNAupmFGHqyeUocw+0uHIOrHUN3gfeKdoCAsCMKVaD+XoCOlKTKWcPKYqVGWADGWAGlY5QmZPvU9kd4KjWrSo94C0FrfojrpXLUJ-t8WRA2U2aGC2VMlA2B54LkJ3km6c6aI7bUOzS6EzaIwS40YeceaeesvYX7SB7drYHpS+0lOo6CHx-gV-iCFEcLGWxRZW4ipBcUgKxSwSWvCSAey8MCw0BULk7dFuOK3FqCwdeW6AwispbRWm1kmx0UH7PkEoKlOpOUBpBHUUHpZrtkPjOvGkrh05cdi5VlUSmx3nc7UlMU7VQ3opqoMiwCEeKUHkN8aDSdeDRdTDWAPFyscaMLEzc0CzbLElMLZJGWMMuFG8MTb4EAA */
  id: 'Transcription',
  initial: 'Idle',
  context: ({ input }) => {
    // Legacy support for the fact that this machine allowed
    // undefined audio formats
    const supportedAudioFormat = (() => {
      try {
        return getSupportedAudioFormat();
      } catch {
        return undefined;
      }
    })();

    let finalizingAudioTimeoutInMs = 0;
    if (input.finalizingAudioTimeoutInMs === undefined) {
      finalizingAudioTimeoutInMs = input.isUsingWebWorkerAudioRecording
        ? FINALIZING_RECORDING_TIMEOUT_FOR_WEBWORKER_MS
        : FINALIZING_RECORDING_TIMEOUT_MS;
    } else {
      finalizingAudioTimeoutInMs = input.finalizingAudioTimeoutInMs;
    }

    return {
      ...input,
      audioChunkFiles: [],
      recordingRetryCount: 0,
      recordingFailed: false,
      supportedAudioFormat,
      recordingStartedAt: new Date().toISOString(),
      userPresenceResponseWindowMs: INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS,
      userPresenceTimeoutMs: INITIAL_USER_PRESENCE_TIMEOUT_MS,
      confidenceScoreWindow: [],
      isLowConfidenceTranscription: false,
      recordingFileName: '',
      resumableUploadSessionUri: undefined,
      nextChunkResumableUploadSessionUri: undefined,
      filesPendingCompletion: [],
      totalRecordingDurationMs: 0,
      isRecordingCleanupFinalized: false,
      isPausing: false,
      isStopping: false,
      hasValidAudioSource: undefined,
      finalizingAudioTimeoutInMs,
    };
  },
  states: {
    Idle: {
      entry: [
        emit({
          type: 'idle',
        }),
      ],
      on: {
        START: [
          {
            target: 'Idle',
            guard: 'hasNonSupportedBrowser',
            actions: [
              {
                type: 'toast',
                params: {
                  type: 'error',
                  message:
                    'Unsupported Browser: Eluve supports Chrome, Safari, and Edge for live transcription.',
                },
              },
              'captureException',
            ],

            description: `If recording isn't supported we should alert the user and transition back to an idle state`,
          },
          {
            target: 'InSession',
          },
        ],
        AUDIO_VALIDATION_REQUESTED: 'ValidatingAudioCapture',
      },
    },

    InSession: {
      entry: ['setSession', 'setupMediaStreams'],

      states: {
        Recording: {
          invoke: [
            {
              src: 'preventBrowserClose',
            },
            {
              src: 'keepScreenAwake',
              input: ({ context }) => ({
                logger: createCorrelatedLogger(context.logger, {
                  appointmentId: context.sessionAppointmentId!,
                }).log,
              }),
            },
            {
              src: 'lockManagerActor',
              id: LOCK_MANAGER_ACTOR,
              input: ({ context }) => ({
                logger: createCorrelatedLogger(context.logger, {
                  appointmentId: context.sessionAppointmentId!,
                }).log,
              }),
            },
          ],

          on: {
            RECONNECT: 'Reconnecting',

            'fileSystem.fileReady': {
              actions: 'processCompletedChunk',
              description: `A file has been finalized and is ready to be sent off to STT processor to be transcribed`,
            },

            'fileSystem.rollingToNewChunk': {
              actions: 'onRollingToNewChunk',
            },

            'fileSystem.fileCreated': {
              actions: 'fileCreated',
              description: `A new file has been created which will need to be eventually uploaded. This could be a single batch file for an entire recording or files that are chunked as the recording progresses.`,
            },
            CLIENT_AUDIO_COMPLETED_AT: {
              actions: ['onClientAudioCompletedAt'],
            },
          },

          entry: [emit({ type: 'recording.started' })],
          exit: ['stopRecording'],

          states: {
            MediaRecorder: {
              invoke: {
                id: MEDIA_RECORDER_ACTOR,
                src: 'mediaRecorder',

                input: ({ context }) => {
                  const { log } = createCorrelatedLogger(context.logger, {
                    appointmentId: context.sessionAppointmentId!,
                  });

                  return {
                    tenantId: context.tenantId,
                    userId: context.userId,
                    appointmentId: context.sessionAppointmentId,
                    recordingStartedAt: context.recordingStartedAt,
                    initialFileName: context.recordingFileName,
                    supportedAudioFormat: context.supportedAudioFormat!,
                    audioPacketSubject: context.audioPacketSubject!,
                    deviceId: context.deviceId,
                    logger: log,
                    db: context.db,
                    MuteDetector,
                    resumableUploadSessionUri:
                      context.resumableUploadSessionUri,
                    nextChunkResumableUploadSessionUri:
                      context.nextChunkResumableUploadSessionUri,
                    chunkDurationInSeconds:
                      context.chunkedBatchTranscriptionEnabled
                        ? context.chunkDurationMinutes
                          ? context.chunkDurationMinutes * 60
                          : undefined
                        : undefined,
                    datadogEnv: context.datadogEnv,
                    serviceName: context.serviceName,
                  };
                },
              },

              states: {
                Loading: {
                  on: {
                    'recorder.started': {
                      target: 'Recording',
                      actions: 'setMediaStream',
                    },
                  },
                },

                Recording: {
                  on: {},
                },
              },

              initial: 'Loading',

              on: {
                'recorder.error': {
                  target: '#Transcription.InSession.RecordingFailed',
                  actions: ['captureWarning', 'onClientAudioCompletedAt'],
                },

                'microphone.disconnected': {
                  actions: [
                    'onMicrophoneDisconnected',
                    'onClientAudioCompletedAt',
                  ],
                  target: '#Transcription.InSession.Paused',
                  reenter: true,
                },

                'microphone.muted': {
                  actions: ['onMicrophoneMuted'],
                },

                'microphone.unmuted': {
                  actions: ['onMicrophoneUnmuted'],
                },
              },
            },

            AudioFileSystem: {
              invoke: {
                src: 'audioFileSystem',

                input: ({ context }) => ({
                  appointmentId: context.sessionAppointmentId!,
                  fileExtension: context.supportedAudioFormat!.extension,
                  userFileSystem: context.userFileSystem,
                  audioPacketSubject: context.audioPacketSubject!,
                  recordingStartedAt: context.recordingStartedAt,
                  db: context.db,
                  chunkedBatchTranscriptionEnabled:
                    context.chunkedBatchTranscriptionEnabled,
                  chunkDurationMinutes: context.chunkDurationMinutes,
                  logger: createCorrelatedLogger(context.logger, {
                    appointmentId: context.sessionAppointmentId!,
                  }).log,
                  numAudioPacketsConsumedByLocalFile: 0,
                  totalBytesConsumedByLocalFile: 0,
                  initialFileName: context.recordingFileName,
                }),
              },

              states: {
                Loading: {
                  on: {
                    'fileSystem.ready': {
                      target: 'Writing',
                    },
                  },
                },

                Writing: {},
              },

              initial: 'Loading',

              on: {
                'fileSystem.error': {
                  target: '#Transcription.InSession.RecordingFailed',
                  actions: 'captureWarning',
                },
              },
            },

            Websocket: {
              initial: 'CheckingIfEnabled',

              states: {
                CheckingIfEnabled: {
                  always: [
                    {
                      target: 'Enabled',
                      guard: 'isLiveTranscriptionEnabled',
                    },
                    {
                      target: 'Disabled',
                    },
                  ],
                },

                Disabled: {
                  type: 'final',
                },

                Enabled: {
                  invoke: {
                    id: WEBSOCKET_ACTOR,
                    src: 'websocket',

                    input: ({ context }) => ({
                      tenantId: context.tenantId,
                      appointmentId: context.sessionAppointmentId!,
                      format: context.supportedAudioFormat!,
                      segmentId: context.segmentId!,
                      audioPacketSubject: context.audioPacketSubject!,
                      recordingStartedAt: context.recordingStartedAt,
                      numAudioPacketsConsumedByWebSocket: 0,
                      totalBytesConsumedByWebSocket: 0,
                      logger: createCorrelatedLogger(context.logger, {
                        appointmentId: context.sessionAppointmentId!,
                      }).log,
                      apiDomain: context.apiEndpoint,
                    }),
                  },

                  initial: 'Authentication',

                  states: {
                    Authentication: {
                      states: {
                        Loading: {
                          entry: [],
                          invoke: {
                            src: 'getAuthToken',

                            onError: {
                              target: 'Failed',
                              actions: [
                                'setTranscriptionDegraded',
                                'captureException',
                              ],
                            },

                            onDone: {
                              target:
                                '#Transcription.InSession.Recording.Websocket.Enabled.Connection',
                              actions: [
                                assign({
                                  authToken: ({ event }) => event.output.token,
                                }),
                              ],
                            },
                          },
                        },
                        Failed: {},
                      },

                      initial: 'Loading',
                    },

                    Connection: {
                      states: {
                        Loading: {
                          entry: 'startWebSocket',
                          on: {
                            'connection.failed': {
                              target: 'Failed',
                              actions: ['setTranscriptionDegraded'],
                            },
                            'connection.connected': 'Connected',
                          },
                        },

                        Failed: {},
                        Connected: {},
                      },

                      initial: 'Loading',
                    },
                  },

                  on: {
                    'connection.transcript.received': {
                      actions: [
                        'updateTranscription',
                        'updateLowConfidenceState',
                        emit(({ event }) => event),
                      ],
                    },
                    'connection.disconnected.unexpectedly': {
                      actions: ['setTranscriptionDegraded', 'captureWarning'],
                    },
                    'connection.sendAudioPacket.failed': {
                      actions: ['setTranscriptionDegraded', 'captureWarning'],
                    },
                    'connection.unavailable': {
                      /**
                       * In degraded mode, we don't send audio packets to the server.
                       *
                       * TODO(deep/sargis)[ELU-1503]: Remove this workaround once the issue is resolved.
                       * On Chrome, specifically in a session popout, the sound bars are not rendered as the user speaks when in degraded mode.
                       * This issue does not occur in Safari; it works fine there.
                       * This seemingly redundant assignment forces the Waveform component to render the bars correctly in Chrome.
                       * There might be a better solution to this issue.
                       */
                      actions: assign({}),
                    },
                  },
                },
              },
            },

            UserPresence: {
              states: {
                Okay: {
                  after: {
                    userPresenceTimeout: { target: 'Checking' },
                  },
                },
                Checking: {
                  entry: [emit({ type: 'checkingUserPresence' })],
                  after: {
                    userPresenceResponseWindow: {
                      target: '#Transcription.InSession.Paused',
                      actions: 'resetUserPresenceDelays',
                      reenter: true,
                    },
                  },
                  on: {
                    CONTINUE: {
                      target: 'Okay',
                      actions: 'shortenUserPresenceDelays',
                    },
                    PAUSE: {
                      target: '#Transcription.InSession.Paused',
                      actions: ['resetUserPresenceDelays'],
                    },
                  },
                },
              },
              initial: 'Okay',
            },

            PendingNextChunk: {
              initial: 'Idle',
              states: {
                Idle: {
                  on: {
                    'fileSystem.fileCreated': {
                      target: 'ProvisioningNextChunk',

                      guard: ({ event }) => {
                        return event.chunkNum > 0;
                      },

                      actions: 'fileCreated',
                    },
                  },
                },

                ProvisioningNextChunk: {
                  invoke: {
                    src: 'prepareAppointmentAudioRecords',

                    input: ({
                      event,
                      context: {
                        tenantId,
                        sessionAppointmentId,
                        isUsingResumableUploads,
                        microphoneName,
                      },
                    }) => {
                      assertEvent(event, 'fileSystem.fileCreated');

                      return {
                        tenantId,
                        appointmentId: sessionAppointmentId,
                        fileName: event.fileName,
                        useResumableUploads: isUsingResumableUploads,
                        userAgent: navigator.userAgent,
                        microphoneName,
                      };
                    },

                    onDone: {
                      target: 'Idle',
                      actions: [
                        enqueueActions(({ enqueue, event, context }) => {
                          enqueue.assign({
                            ...event.output,
                          });

                          const { nextChunkResumableUploadSessionUri } =
                            event.output;

                          const currentChunkNum = getChunkNumberFromFileName(
                            context.lastObservedChunkFileName ?? '',
                          );

                          if (
                            nextChunkResumableUploadSessionUri &&
                            currentChunkNum !== null
                          ) {
                            enqueue.sendTo(MEDIA_RECORDER_ACTOR, {
                              type: 'next_chunk_resumable_upload_session_uri_available',
                              nextChunkResumableUploadSessionUri,
                            } satisfies MediaRecorderEvents);
                          }
                        }),
                      ],
                    },
                    onError: {
                      target: 'Idle',
                      actions: [
                        'captureWarning',
                        enqueueActions(({ context, enqueue }) => {
                          if (
                            context.appointmentId &&
                            context.lastObservedChunkFileName
                          ) {
                            enqueue({
                              type: 'scheduleBackgroundTask',
                              params: {
                                type: 'TASK.PREPARE_AUDIO_RECORDS',
                                appointmentId: context.appointmentId!,
                                fileName: context.lastObservedChunkFileName,
                                useResumableUploads:
                                  context.isUsingResumableUploads,
                                origin: context.clientOrigin,
                              },
                            });
                          }
                        }),
                      ],
                    },
                  },
                },
              },
            },

            FinalizingRecording: {
              states: {
                Idle: {
                  on: {
                    PAUSE: {
                      target: 'Finalizing',
                      actions: ['onPausing'],
                    },

                    STOP: {
                      target: 'Finalizing',
                      actions: ['onStopping'],
                    },
                  },
                },

                Finalizing: {
                  always: [
                    {
                      target: '#Transcription.InSession.Paused',
                      guard: 'pauseFinalized',
                    },
                    {
                      target: '#Transcription.InSession.SessionEnded',
                      guard: 'stopFinalized',
                    },
                  ],

                  after: {
                    finalizingAudioTimeout: [
                      {
                        target: '#Transcription.InSession.Paused',
                        guard: 'isPausing',
                      },
                      {
                        target: '#Transcription.InSession.SessionEnded',
                        guard: 'isStopping',
                      },
                    ],
                  },
                },
              },

              initial: 'Idle',

              on: {
                'recorder.cleanup_finalized': {
                  actions: [
                    assign({
                      isRecordingCleanupFinalized: true,
                    }),
                  ],
                },
              },
            },
          },

          type: 'parallel',
        },

        Paused: {
          entry: ['onPaused'],
          on: {
            RESUME: {
              target: 'PreparingRecording',
              reenter: true,
              actions: ['setDeviceId'],
            },

            STOP: {
              target: 'SessionEnded',
            },

            DISMISS_SESSION: {
              target: '#Transcription.Idle',
            },
          },
        },

        RecordingFailed: {
          entry: [
            {
              type: 'toast',
              params: {
                type: 'error',
                message: 'Recording failed',
                // In case failed retried multiple times, giving id will prevent duplicate toasts
                id: 'recording-failed',
              },
            },
            'incrementRecordingRetryCount',
          ],

          always: [
            {
              target: 'PreparingRecording',
              guard: 'isFirstRecordingFailure',
              reenter: true,
            },
            {
              target: 'Paused',
              actions: ['onRecordingFailed', 'captureException'],
              reenter: true,
            },
          ],
        },

        SessionEnded: {
          always: {
            target: '#Transcription.Idle',
            reenter: true,
          },
        },

        BeforeRecording: {
          always: [
            {
              target: 'PlayingDisclaimer',
              guard: 'needsToPlayDisclaimer',
              reenter: true,
            },
            {
              target: 'PreparingRecording',
              reenter: true,
            },
          ],
        },

        PlayingDisclaimer: {
          entry: 'playDisclaimerSound',
          on: {
            'PLAY.DISCLAIMER.ENDED': {
              target: 'PreparingRecording',
              reenter: true,
            },
          },
        },

        Reconnecting: {
          always: {
            target: 'Recording',
            reenter: true,
          },
        },

        PreparingRecording: {
          entry: 'prepareRecording',
          invoke: {
            src: 'prepareAppointmentAudioRecords',
            input: ({
              context: {
                recordingFileName,
                tenantId,
                sessionAppointmentId,
                isUsingResumableUploads,
                microphoneName,
              },
            }) => ({
              tenantId,
              appointmentId: sessionAppointmentId,
              fileName: recordingFileName,
              useResumableUploads: isUsingResumableUploads,
              userAgent: navigator.userAgent,
              microphoneName,
            }),
            onDone: {
              target: 'Recording',
              actions: assign(({ event: { output } }) => ({
                ...output,
              })),
            },
            onError: {
              target: 'Recording',
              actions: [
                'captureWarning',
                enqueueActions(({ context, enqueue }) => {
                  if (context.appointmentId) {
                    enqueue({
                      type: 'scheduleBackgroundTask',
                      params: {
                        type: 'TASK.PREPARE_AUDIO_RECORDS',
                        appointmentId: context.appointmentId!,
                        fileName: context.recordingFileName,
                        useResumableUploads: context.isUsingResumableUploads,
                        origin: context.clientOrigin,
                        userAgent: navigator.userAgent,
                        microphoneName: context.microphoneName,
                      },
                    });
                  }
                }),
              ],
            },
          },
        },
      },

      initial: 'BeforeRecording',
      exit: 'clearSession',
    },

    ValidatingAudioCapture: {
      entry: [assign({ hasValidAudioSource: undefined })],
      invoke: {
        src: 'validateAudioCapture',

        input: ({ event, context }) => {
          assertEvent(event, 'AUDIO_VALIDATION_REQUESTED');
          return {
            deviceId: event.deviceId,
            logger: context.logger,
          };
        },
      },

      on: {
        AUDIO_VALIDATION_REQUESTED: {
          target: 'ValidatingAudioCapture',
          reenter: true,
          description: `Re-run validation if a new microphone is requested`,
          guard: ({ event, context }) => event.deviceId !== context.deviceId,
        },

        ['audio.soundDetected']: {
          actions: ['onAudioSoundDetected'],
          target: 'Idle',
        },
        ['audio.silent']: {
          actions: ['onAudioSilent'],
        },
      },
    },
  },
});

export type TranscriptionMachineActor = ActorRefFrom<
  typeof transcriptionMachine
>;

export type TranscriptionMachineSnapshot = SnapshotFrom<
  typeof transcriptionMachine
>;
